import { GetServerSideProps, GetStaticPropsResult } from 'next';
import { useRouter } from 'next/router';

import DiscoverPage from '@/domains/Discover';
import { DEFAULT_USER_ROLE } from '@/domains/Discover/DiscoverPage';
import { FilterValues } from '@/domains/Groups/constants/FilterValues';
import PageLayout from '@/widgets/PageLayout';
import { Group } from '@/types/Content/Group';
import { Product } from '@/types/Content/Product/Product';
import productService from '@/services/productService';
import { getPersonalizedTag } from '@/domains/home/utils';
import { getPersonalizedTagCancer } from '@/utils/getPersonalizedCancerTagCancer';
import { ProductsParams } from '@/types/Content/Product/ProductParams';
import Discussion from '@/types/Discussions';
import discussionService from '@/services/discussionService';
import subscriptionGroupService, {
  ISubscriptionGroupQuery,
} from '@/services/subscriptionGroupService';
import { HTTPListResponse } from '@/types/HTTPListResponse';
import { getStaticPropsForUnauthorizedUser } from '@/utils/getStaticPropsForUnauthorizedUser';

type PageProps = {
  products: Product[];
  groups: { data: HTTPListResponse<Group>; params: ISubscriptionGroupQuery };
  productsParams: ProductsParams;
  discussions: { data: Discussion[]; tags: string[] };
};

function Discover({
  products = [],
  groups,
  productsParams = { page: 0, tags: [] },
  discussions = {
    data: [],
    tags: [],
  },
}: PageProps): JSX.Element {
  const router = useRouter();

  let filter = router.query?.filters;

  filter = Array.isArray(filter) ? filter.shift() : filter;

  const filterType = Object.values(FilterValues).includes(
    filter as FilterValues,
  )
    ? filter
    : undefined;

  return (
    <DiscoverPage
      filterType={filterType}
      groups={groups}
      products={products}
      productsParams={productsParams}
      discussions={discussions}
    />
  );
}

const unauthorizedStaticProps: () => Promise<
  GetStaticPropsResult<PageProps>
> = async () => {
  const promises = [];
  const userTags = [
    getPersonalizedTag(DEFAULT_USER_ROLE),
    getPersonalizedTagCancer('all'),
  ];
  const productsParams = {
    page: 0,
    tags: userTags,
  };

  const groupsParams = {
    page: 0,
    tags: userTags,
    contentTypeList: '',
    contentTrending: true,
    includeTotalCount: true,
  };

  promises.push(subscriptionGroupService.getAll(groupsParams));

  promises.push(productService.getAll(productsParams));
  promises.push(discussionService.getDiscussions({ tags: userTags }));

  const results = await Promise.allSettled(promises);

  const [groupResults, productsResults, discussionsResult] = results.map(
    (promise) => {
      if (promise.status === 'rejected') {
        return null;
      }
      return promise.value;
    },
  );

  const groups = {
    data: groupResults,
    params: groupsParams,
  };
  const products = productsResults?.data?.results ?? [];
  const discussions = {
    data: discussionsResult ?? [],
    tags: userTags,
  };

  return {
    props: {
      groups,
      products,
      productsParams,
      discussions,
    },
    revalidate: 60,
  };
};

export const getStaticProps: GetServerSideProps = async (ctx) => {
  const defaultServerSideProps = {
    props: {
      products: [],
      groups: {
        data: { results: [] },
        params: {
          page: 0,
          tags: [],
          contentTypeList: '',
          contentTrending: true,
          includeTotalCount: true,
        },
      },
      productsParams: {
        page: 0,
        tags: [],
      },
      discussions: { data: [], tags: [] },
    },
  };

  return getStaticPropsForUnauthorizedUser(
    unauthorizedStaticProps,
    ctx,
    defaultServerSideProps,
  );
};

export default Discover;
